import React, { useState, useEffect } from 'react';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { FileText, Bank, CheckCircle, Clock, Folder, Trash } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function ClientDocuments() {
  const [documents, setDocuments] = useState(null);
  const [otherDocs, setOtherDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocuments = async () => {
    try {
      const [docsRes, otherRes] = await Promise.all([
        clientApi.get('/documents'),
        clientApi.get('/other-documents'),
      ]);
      setDocuments(docsRes.data);
      setOtherDocs(otherRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOtherDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await clientApi.delete(`/other-documents/${docId}`);
      setOtherDocs(otherDocs.filter((d) => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const purchaseInvoices = documents?.invoices?.filter((inv) => inv.invoice_type === 'purchase') || [];
  const salesInvoices = documents?.invoices?.filter((inv) => inv.invoice_type === 'sales') || [];
  const bankStatements = documents?.bank_statements || [];

  return (
    <div className="space-y-6" data-testid="client-documents-page">
      <div>
        <h1 className="heading-2 mb-1">My Documents</h1>
        <p className="text-muted-foreground">View all your uploaded documents and their status</p>
      </div>

      <Tabs defaultValue="purchase" className="space-y-6">
        <TabsList>
          <TabsTrigger value="purchase" data-testid="tab-purchase">
            <FileText className="w-4 h-4 mr-2" />
            Purchase Invoices ({purchaseInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">
            <FileText className="w-4 h-4 mr-2" />
            Sales Invoices ({salesInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">
            <Bank className="w-4 h-4 mr-2" />
            Bank Statements ({bankStatements.length})
          </TabsTrigger>
          <TabsTrigger value="other" data-testid="tab-other">
            <Folder className="w-4 h-4 mr-2" />
            Other Documents ({otherDocs.length})
          </TabsTrigger>
        </TabsList>

        {/* Purchase Invoices */}
        <TabsContent value="purchase">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Purchase Invoices</CardTitle>
              <CardDescription>Invoices from your suppliers and vendors</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {purchaseInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No purchase invoices uploaded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>File Name</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseInvoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`purchase-row-${invoice.id}`}>
                        <TableCell className="font-medium">{invoice.original_filename || 'Invoice'}</TableCell>
                        <TableCell>{invoice.supplier_name || '-'}</TableCell>
                        <TableCell>{invoice.invoice_date || formatDate(invoice.created_at)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={invoice.status === 'matched' ? 'status-matched' : 'status-pending'}
                          >
                            {invoice.status === 'matched' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Matched</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" /> Pending</>
                            )}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Invoices */}
        <TabsContent value="sales">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Sales Invoices</CardTitle>
              <CardDescription>Invoices you've issued to customers</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {salesInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No sales invoices uploaded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>File Name</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesInvoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`sales-row-${invoice.id}`}>
                        <TableCell className="font-medium">{invoice.original_filename || 'Invoice'}</TableCell>
                        <TableCell>{invoice.supplier_name || '-'}</TableCell>
                        <TableCell>{invoice.invoice_date || formatDate(invoice.created_at)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={invoice.status === 'matched' ? 'status-matched' : 'status-pending'}
                          >
                            {invoice.status === 'matched' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Matched</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" /> Pending</>
                            )}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Statements */}
        <TabsContent value="statements">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Bank Statements</CardTitle>
              <CardDescription>Your uploaded bank statements</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {bankStatements.length === 0 ? (
                <div className="text-center py-12">
                  <Bank className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No bank statements uploaded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>File Name</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead>Transactions</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bankStatements.map((statement) => (
                      <TableRow key={statement.id} data-testid={`statement-row-${statement.id}`}>
                        <TableCell className="font-medium">{statement.original_filename || 'Statement'}</TableCell>
                        <TableCell>{formatDate(statement.created_at)}</TableCell>
                        <TableCell>{statement.transactions_count || 0}</TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={statement.status === 'processed' ? 'status-onboarded' : 'status-pending'}
                          >
                            {statement.status === 'processed' ? 'Processed' : 'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Other Documents */}
        <TabsContent value="other">
          <Card className="border border-border">
            <CardHeader className="pb-4">
              <CardTitle className="heading-4">Other Documents</CardTitle>
              <CardDescription>Contracts, receipts, ID copies, tax returns, and other files</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {otherDocs.length === 0 ? (
                <div className="text-center py-12">
                  <Folder className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No other documents uploaded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Document Name</TableHead>
                      <TableHead>File Type</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {otherDocs.map((doc) => (
                      <TableRow key={doc.id} data-testid={`other-doc-row-${doc.id}`}>
                        <TableCell className="font-medium">{doc.document_name || doc.original_filename}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{(doc.file_type || '').toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(doc.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDeleteOtherDoc(doc.id)}
                            data-testid={`delete-other-doc-${doc.id}`}
                          >
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
